<template>
  <div>
    <portal to="subheader-left">
      <div class="d-flex align-items-center">
        <h5 class="text-dark font-weight-bold my-5">Transferplanung für gemeinsame Reisen</h5>
      </div>
    </portal>

    <div class="card card-custom">
      <div class="card-header border-0 py-4 px-6">
        <div class="card-title">
          <b-button-group size="sm" style="gap: 2px">
            <b-button
              v-for="jahr in jahre"
              :key="jahr"
              :variant="selectedJahr === jahr ? 'primary' : 'outline-secondary'"
              @click="selectedJahr = jahr"
            >
              {{ jahr }}
            </b-button>
          </b-button-group>
        </div>
      </div>

      <div class="card-body px-6 py-4">
        <b-overlay :show="isLoading" rounded="sm">
          <div class="row">
            <div v-for="typ in ['hin', 'rueck']" :key="typ" class="col-6">
              <div class="border rounded p-4">
                <div class="d-flex justify-content-between align-items-center mb-4">
                  <h6 class="font-weight-bold mb-0">
                    {{ typ === 'hin' ? 'Transfer Hin' : 'Transfer Rück' }}
                  </h6>
                  <b-button variant="light-primary" size="sm" @click="addGruppe(typ)">
                    <i class="fas fa-plus mr-1"></i>
                    Neue Gruppe
                  </b-button>
                </div>

                <div
                  v-for="(gruppe, index) in getList(typ)"
                  :key="gruppe.id || typ + '-' + index"
                  class="d-flex align-items-center mb-3"
                  style="gap: 8px"
                >
                  <AppDatePicker
                    v-model="gruppe.von"
                    value-type="YYYY-MM-DD"
                    format="DD.MM."
                    :min="minDate"
                    :max="maxDate"
                    :state="gruppe._saved ? null : gruppe.von ? null : false"
                    placeholder="Von"
                    size="sm"
                    style="flex: 0 0 150px"
                    @input="gruppe._saved = false"
                  />
                  <AppDatePicker
                    v-model="gruppe.bis"
                    value-type="YYYY-MM-DD"
                    format="DD.MM."
                    :min="gruppe.von || minDate"
                    :max="maxDate"
                    :state="gruppe._saved ? null : gruppe.bis && gruppe.bis > gruppe.von ? null : false"
                    placeholder="Bis"
                    size="sm"
                    style="flex: 0 0 150px"
                    @input="gruppe._saved = false"
                  />
                  <AppSelect
                    v-model="gruppe.reisekuerzel"
                    :options="alleReisekuerzel"
                    label="Reisen"
                    multiple
                    placeholder="Reisen auswählen"
                    no-results-text="Keine Reisen gefunden."
                    empty-text="Keine Reisen verfügbar."
                    class="flex-grow-1"
                    :state="reisekuerzelState(gruppe)"
                    @input="gruppe._saved = false"
                  />
                  <b-button
                    v-if="!gruppe._saved"
                    size="sm"
                    variant="primary"
                    @click="saveGruppe(gruppe, index, typ)"
                    :disabled="!isGruppeValid(gruppe)"
                  >
                    <i class="fas fa-save p-0"></i>
                  </b-button>
                  <b-button
                    size="sm"
                    variant="danger"
                    @click="deleteGruppe(gruppe, index, typ)"
                    style="height: 31.84px"
                  >
                    <i class="fas fa-trash p-0"></i>
                  </b-button>
                </div>

                <div v-if="getList(typ).length === 0" class="text-muted text-center py-4">
                  Keine Gruppen vorhanden.
                </div>
              </div>
            </div>
          </div>
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import AppSelect from '@/components/common/app-select/app-select.vue';
import AppDatePicker from '@/components/common/app-date-picker/app-date-picker.vue';
import apiService from '@/core/common/services/api.service';
import { mapGetters } from 'vuex';
import { GET_REISEN_KUERZEL_FILTER_OPTIONS } from '@/core/produkte/reisen/stores/reisen.module';

export default {
  name: 'TransferplanungView',
  components: {
    AppDatePicker,
    AppSelect,
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      jahre: [currentYear, currentYear + 1, currentYear + 2],
      selectedJahr: currentYear,
      gruppenHin: [],
      gruppenRueck: [],
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters(['getReisekuerzelOptions']),
    alleReisekuerzel() {
      return this.getReisekuerzelOptions.map(r => r.reisekuerzel).sort();
    },
    alleGruppen() {
      return [...this.gruppenHin, ...this.gruppenRueck];
    },
    minDate() {
      return `${this.selectedJahr}-01-01`;
    },
    maxDate() {
      return `${this.selectedJahr}-12-31`;
    },
  },
  watch: {
    selectedJahr() {
      this.loadGruppen();
    },
  },
  created() {
    this.$store.dispatch(GET_REISEN_KUERZEL_FILTER_OPTIONS);
    this.loadGruppen();
  },
  methods: {
    reisekuerzelState(gruppe) {
      if (gruppe._saved) {
        return null;
      }
      return gruppe.reisekuerzel && gruppe.reisekuerzel.length >= 2 ? null : false;
    },
    isGruppeValid(gruppe) {
      return (
        gruppe.reisekuerzel &&
        gruppe.reisekuerzel.length >= 2 &&
        !!gruppe.von &&
        !!gruppe.bis &&
        gruppe.bis > gruppe.von
      );
    },
    getAvailableOptions(currentGruppe) {
      const usedByOtherGroups = new Set();
      this.alleGruppen.forEach(g => {
        if (g !== currentGruppe) {
          (g.reisekuerzel || []).forEach(k => usedByOtherGroups.add(k));
        }
      });
      return this.alleReisekuerzel.filter(k => !usedByOtherGroups.has(k));
    },
    toIso(dateStr) {
      return dateStr ? `${dateStr}T00:00:00` : '';
    },
    fromIso(isoStr) {
      return isoStr ? isoStr.substring(0, 10) : '';
    },
    async loadGruppen() {
      this.isLoading = true;
      try {
        const von = `${this.selectedJahr}-01-01`;
        const bis = `${this.selectedJahr}-12-31`;
        const response = await apiService.get(
          `GemeinsameTransferplanung?$filter=Von ge ${von} and Von le ${bis}&$expand=reisen($select=reisekuerzel)`
        );
        const data = response.data?.result?.value || [];
        const mapped = data.map(item => ({
          id: item.id,
          reisekuerzel: (item.reisen || []).map(r => r.reisekuerzel),
          typ: item.typ,
          von: this.fromIso(item.von),
          bis: this.fromIso(item.bis),
          _saved: true,
        }));
        this.gruppenHin = mapped.filter(g => g.typ === 'Hin');
        this.gruppenRueck = mapped.filter(g => g.typ === 'Rueck');
      } catch {
        this.gruppenHin = [];
        this.gruppenRueck = [];
      } finally {
        this.isLoading = false;
      }
    },
    addGruppe(typ) {
      const gruppe = { id: null, reisekuerzel: [], typ, von: '', bis: '', _saved: false };
      if (typ === 'hin') {
        this.gruppenHin.push(gruppe);
      } else {
        this.gruppenRueck.push(gruppe);
      }
    },
    getList(typ) {
      return typ === 'hin' ? this.gruppenHin : this.gruppenRueck;
    },
    async saveGruppe(gruppe, index, typ) {
      this.isLoading = true;
      try {
        if (gruppe.id) {
          await apiService.put(`GemeinsameTransferplanung/${gruppe.id}`, {
            reisekuerzel: Array.from(gruppe.reisekuerzel),
            von: this.toIso(gruppe.von),
            bis: this.toIso(gruppe.bis),
          });
          this.$bvToast.toast('Gruppe wurde aktualisiert.', { variant: 'success', noCloseButton: true });
        } else {
          await apiService.post('GemeinsameTransferplanung', {
            reisekuerzel: Array.from(gruppe.reisekuerzel),
            typ: typ === 'hin' ? 'Hin' : 'Rueck',
            von: this.toIso(gruppe.von),
            bis: this.toIso(gruppe.bis),
          });
          this.$bvToast.toast('Gruppe wurde erstellt.', { variant: 'success', noCloseButton: true });
        }
        await this.loadGruppen();
      } catch {
        this.$bvToast.toast('Fehler beim Speichern der Gruppe.', { variant: 'danger', noCloseButton: true });
      } finally {
        this.isLoading = false;
      }
    },
    async deleteGruppe(gruppe, index, typ) {
      if (!gruppe.id) {
        this.getList(typ).splice(index, 1);
        return;
      }
      this.isLoading = true;
      try {
        await apiService.delete(`GemeinsameTransferplanung/${gruppe.id}`);
        this.getList(typ).splice(index, 1);
        this.$bvToast.toast('Gruppe wurde gelöscht.', { variant: 'success', noCloseButton: true });
      } catch {
        this.$bvToast.toast('Fehler beim Löschen der Gruppe.', { variant: 'danger', noCloseButton: true });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
